/**
 * CPK-5103 — client side of the is_document check (server: lambdas/src/endpoint/core/kyc-is-document).
 *
 * The check is configured per action in `cp_actions.config.isDocumentCheck`; the server owns the
 * decision knobs (model, safety valve, confidence bar), the client only these two:
 *  - `clientTimeoutMs` (default 10000): how long the user waits on the spinner before the image is
 *    let through anyway (fail-open);
 *  - `maxImageEdge` (default 1024): the capture is downscaled to this longest side before upload —
 *    enough to tell "document or not", and keeps the request a few hundred KB.
 */

export interface IsDocumentClientConfig {
  enabled: boolean;
  clientTimeoutMs: number;
  maxImageEdge: number;
}

export const IS_DOCUMENT_CLIENT_DEFAULTS: Omit<IsDocumentClientConfig, 'enabled'> = {
  clientTimeoutMs: 10000,
  maxImageEdge: 1024,
};

function boundedInt(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Normalise the raw `isDocumentCheck` blob; anything malformed leaves the check off / at defaults. */
export function readIsDocumentClientConfig(raw: unknown): IsDocumentClientConfig {
  const cfg = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    enabled: cfg['enabled'] === true,
    clientTimeoutMs: boundedInt(cfg['clientTimeoutMs'], IS_DOCUMENT_CLIENT_DEFAULTS.clientTimeoutMs, 2000, 25000),
    maxImageEdge: boundedInt(cfg['maxImageEdge'], IS_DOCUMENT_CLIENT_DEFAULTS.maxImageEdge, 512, 2048),
  };
}

/** Re-encode a capture (data URL) as a JPEG no larger than `maxEdge`, returned as bare base64. */
export async function toJpegBase64(dataUrl: string, maxEdge: number, quality = 0.85): Promise<string> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
  const scale = Math.min(1, maxEdge / longest);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const out = canvas.toDataURL('image/jpeg', quality);
  canvas.width = canvas.height = 0; // release the pixels now, not at GC (low-end phones)
  return out.slice(out.indexOf(',') + 1);
}

/** Reject with `Error('timeout')` when `promise` hasn't settled within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
